import { makeAutoObservable } from 'mobx'
import { restaurantCenter, restaurantConfig, RESTAURANT_EVENT, storeMobx, cartMobx, queueMobx } from 'restaurant'
import { getPlatform, getHistoryNew, alertLayer, miniAppRouteTo } from '@utils'
import { store, addParams, delParams } from '@dt/fe-utils'
import { debounce } from '@dt/fe-utils/dist/lodash'
import { QueueOrderingController } from '@ctrls/index'
import { QueueAPI, DeskAPI } from 'api/index'

const { inMiniApp, isWeixinMiniProgram, isAliPayMiniProgram } = getPlatform()

class QueueOrderingMobx {
  constructor () {
    makeAutoObservable(this)

    // 防抖
    const _saveCart = debounce(() => {
      this.saveCart()
    }, 500)

    // 监听购物车变化
    restaurantCenter.on([
      RESTAURANT_EVENT.CART_ITEMS_CHANGED,
      RESTAURANT_EVENT.CART_ITEMS_BATCH_CHANGED,
      RESTAURANT_EVENT.CART_INFO_CHANGED,
      RESTAURANT_EVENT.CART_PREORDER_INFO_CHANGED,
    ], () => {
      if (this.inPreQueueOrdering) {
        _saveCart()
      }
    })
  }
  /** 手动处理 */
  handlerFlag: boolean = false
  /** 排队点餐socket实例 */
  queueOrderingSocket: QueueOrderingController
  /** 排队预点餐 */
  get inPreQueueOrdering () {
    const { queueInfo } = queueMobx
    return Config.codeType === 'store' && restaurantConfig.queueId && queueInfo?.callId && ['Waiting', 'Calling'].includes(queueInfo.status)
  }
  /** 连接多人点餐服务 */
  async connect (storeId: number, queueId: number) {
    if (!storeId || !queueId) return
    // socket
    this.queueOrderingSocket = new QueueOrderingController(storeId, queueId)

    // 监听页面显隐，重新拉取数据（解决socket断连问题）
    window.addEventListener('visibilitychange', () => {
      let hiddenTime = 0
      if (document.visibilityState === 'hidden') {
        hiddenTime = Date.now()
      } else {
        // 隐藏超过3s再拉取
        if ((Date.now() - hiddenTime) / 1000 > 3) {
          this.getQueueInfo()
        }
      }
    })
  }
  /** 监听socket emit 事件 */
  onEmit () {
    this.queueOrderingSocket?.on('emit', (data: IQueueInfo) => {
      const { status, sort, expireTime } = data
      const { queueInfo } = queueMobx
      if (queueInfo?.status !== status || (sort !== undefined && queueInfo?.sort !== sort) || queueInfo?.expireTime !== expireTime) {
        this.resetQueueInfo(data)
      }
      if (data?.status === 'Checkin' && !this.handlerFlag) {
        this.toTable(data)
      }
    })
  }
  /** 清除排队信息 */
  clearQueueInfo (status?: TQueueStatus) {
    const history = getHistoryNew()
    if (!status || ['Pass', 'Checkin'].includes(status)) {
      store.remove(`ls.queueId.${restaurantConfig.brandId}.${restaurantConfig.storeId}`)
      if (status !== 'Checkin') {
        Config.queueId = 0
        restaurantCenter.setConfig({ queueId: 0 })
      }
      if (status === 'Pass') {
        alertLayer({
          message: i18n.t('queue.queueToReCall'),
          backdropDismiss: false,
          button: i18n.t('queue.reCall')
        }).then(() => {
          if (inMiniApp && isWeixinMiniProgram) {
            miniAppRouteTo({
              url: '/pages/queue/index'
            })
            return
          } else if (inMiniApp && isAliPayMiniProgram) {
            miniAppRouteTo({
              url: '/pages/queue/index'
            })
            return
          }
          location.replace(delParams({ key: 'queueId' }).replace(location.pathname, '/queue'))
        })
      }
    }
  }
  /** 获取排队信息排队信息 */
  async getQueueInfo () {
    try {
      const res = await queueMobx.getCallInfo()
      const inQueue = !!res && !res?.error && !(Config.codeType === 'table' && Number(res?.tableCode) !== Number(Config.TABLECODE))
      if (inQueue) {
        if (res.callId) {
          Config.queueId = res.callId
          restaurantCenter.setConfig({ queueId: res.callId })
          this.clearQueueInfo(res.status)
          this.setCartInfo()
        } else {
          this.clearQueueInfo(Config.queueId ? 'Pass' : null)
          cartMobx.setCallId()
        }
      } else {
        this.clearQueueInfo()
        cartMobx.setCallId()
      }
      return res
    } catch (error) {
      this.clearQueueInfo()
      cartMobx.setCallId()
    }
  }
  /** 更新取号信息 */
  resetQueueInfo (data: IQueueInfo) {
    queueMobx.resetQueueInfo(data)
    Config.queueId = data.callId
    restaurantCenter.setConfig({ queueId: data.callId })
    this.clearQueueInfo(data.status)
    this.setCartInfo()
  }
  /** 保存购物车信息 */
  async setCartInfo () {
    if (!restaurantConfig.queueId) {
      return
    }
    const { contactMobile, contactName, person, kids, preOrders, ordersPreId, tableCode } = queueMobx.queueInfo
    if (restaurantConfig.codeType === 'table' && Number(restaurantConfig.tableCode) !== Number(tableCode)) {
      return
    }
    cartMobx.setCallId(restaurantConfig.queueId)
    cartMobx.setOrdersPreId(ordersPreId)
    cartMobx.setPayMode(storeMobx.storeSceneConfig.payMode)
    if (!preOrders) {
      cartMobx.setEatType('dinein')
      cartMobx.setJiuCanNum(person, kids)
      cartMobx.setContact({
        contactName,
        contactMobile
      })
    } else {
      cartMobx.setPreOrdersData(preOrders)
    }
  }
  /** 用户自主入桌 */
  async checkinCallByBuyer (params?: { callId?: number; tableCode?: string; contactName?: string }) {
    if (!restaurantConfig.queueId) {
      return
    }
    const res = await queueMobx.checkinCallByBuyer(params)
    if (!res?.error) {
      this.setHandlerFlag(true)
    }
    return res
  }
  /** 预点单 */
  async saveCart () {
    if (!restaurantConfig.queueId) {
      return
    }
    let saveData = cartMobx.getPreSaveData()
    const res = await QueueAPI.saveBeforeCalling(saveData)
    if (!res?.error) {
      cartMobx.setOrdersPreId(res.ordersPreId)
    }
    return res
  }
  /** 获取桌台预点单信息 */
  async getTableOrder () {
    const orders = await DeskAPI.getTableByTableCode({
      storeId: Config.STOREID,
      tableCode: String(Config.TABLECODE),
    })
    if (!orders?.error) {
      const { preOrders, callId } = orders?.find(item => item.ordersPreId && item.preOrders && !item.ordersId) || {}
      if (preOrders && !cartMobx.info?.ordersPreId && cartMobx.info?.ordersPreId !== preOrders?.ordersPreId) {
        cartMobx.setPreOrdersData(preOrders)
        setTimeout(() => {
          cartMobx.setCallId(callId)
        }, 1500)
      }
    }
  }
  /** 跳转至桌台 */
  toTable (data: IQueueInfo) {
    if (!data?.tableCode) {
      return
    }
    if (Config.codeType === 'table' && Number(Config.TABLECODE) === Number(data.tableCode)) {
      return
    }
    let _url = addParams({
      url: delParams({ key: 'queueId' }).replace(location.pathname, '/menu'),
      params: {
        ct: 'table',
        t: data.tableCode,
      }
    })
    location.replace(_url)
  }
  /** 手动标记 */
  setHandlerFlag (flag: boolean) {
    this.handlerFlag = flag
  }
}

export default new QueueOrderingMobx()
